import {
  SupportTicketFilterNames,
  supportTicketFilterData,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { useHandler } from "@redotech/react-util/hook";
import FilterLinesIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import XCloseIcon from "@redotech/redo-icons/x_filled.svg";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import type { DateFilterData } from "@redotech/redo-model/views/advanced-filters/date-filter";
import type { ExistsFilterData } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { deepCopyObject } from "@redotech/util/object";
import { assertNever } from "@redotech/util/type";
import { memo, useEffect, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { useActiveViewStatusCounts } from "../hooks/use-active-view-status-counts";
import {
  useAddFilter,
  useAdvancedFilters,
  useStatusFilter,
} from "./filters-context";

export type StatusValue =
  | "OPEN"
  | "CLOSED"
  | "IN_PROGRESS"
  | "SNOOZED"
  | "DRAFT";

export const STATUS_OPTIONS: RedoListItem<StatusValue>[] = [
  { id: "OPEN", value: "OPEN", type: "text", text: "Open" },
  { id: "CLOSED", value: "CLOSED", type: "text", text: "Closed" },
  {
    id: "IN_PROGRESS",
    value: "IN_PROGRESS",
    type: "text",
    text: "In progress",
  },
  { id: "SNOOZED", value: "SNOOZED", type: "text", text: "Snoozed" },
  { id: "DRAFT", value: "DRAFT", type: "text", text: "Draft" },
];

type FilterFieldKey =
  | "ASSIGNEES"
  | "CHANNELS"
  | "SUPPORT_TICKET_TAGS"
  | "CLOSED_DATE"
  | "MENTIONS"
  | "CREATED_DATE"
  | "CUSTOMER_TAGS"
  | "LAST_RESPONSE_AT"
  | "READ"
  | "WORDS";

const FILTER_OPTIONS: RedoListItem<FilterFieldKey>[] = [
  { id: "ASSIGNEES", value: "ASSIGNEES", type: "text", text: "Assignees" },
  { id: "CHANNELS", value: "CHANNELS", type: "text", text: "Channel" },
  { id: "READ", value: "READ", type: "text", text: "Read Status" },
  {
    id: "SUPPORT_TICKET_TAGS",
    value: "SUPPORT_TICKET_TAGS",
    type: "text",
    text: "Support ticket tags",
  },
  // { id: "WORDS", value: "WORDS", type: "text", text: "Keywords" },
  {
    id: "CUSTOMER_TAGS",
    value: "CUSTOMER_TAGS",
    type: "text",
    text: "Customer Tags",
  },
  {
    id: "CREATED_DATE",
    value: "CREATED_DATE",
    type: "text",
    text: "Created Date",
  },
  {
    id: "LAST_RESPONSE_AT",
    value: "LAST_RESPONSE_AT",
    type: "text",
    text: "Last Response",
  },
  {
    id: "CLOSED_DATE",
    value: "CLOSED_DATE",
    type: "text",
    text: "Closed Date",
  },
  { id: "MENTIONS", value: "MENTIONS", type: "text", text: "Mentions" },
];

export const AddFilterButton = memo(function AddFilterButton() {
  const addFilter = useAddFilter();

  const [filtersDropdownOpen, setFiltersDropdownOpen] = useState(false);
  const [filtersDropdownButtonRef, setFiltersDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const handleAddFilter = useHandler(
    ({ item }: { item: RedoListItem<FilterFieldKey> }) => {
      const filterKey = item.value;

      switch (filterKey) {
        case "ASSIGNEES": {
          const filter: ArrayStringFilterData = deepCopyObject(
            supportTicketFilterData[
              SupportTicketFilterNames.ASSIGNEES
            ] as ArrayStringFilterData,
          );
          addFilter(filter);
          break;
        }
        case "CHANNELS": {
          const filter: ArrayStringFilterData = deepCopyObject(
            supportTicketFilterData[
              SupportTicketFilterNames.CHANNELS
            ] as ArrayStringFilterData,
          );
          addFilter(filter);
          break;
        }
        case "READ": {
          const filter: ExistsFilterData = deepCopyObject(
            supportTicketFilterData[
              SupportTicketFilterNames.READ_AT
            ] as ExistsFilterData,
          );
          addFilter(filter);
          break;
        }
        case "SUPPORT_TICKET_TAGS": {
          const filter: ArrayStringFilterData = deepCopyObject(
            supportTicketFilterData[
              SupportTicketFilterNames.SUPPORT_TICKET_TAGS
            ] as ArrayStringFilterData,
          );
          addFilter(filter);
          break;
        }
        case "WORDS": {
          const filter: ArrayStringFilterData = deepCopyObject(
            supportTicketFilterData[
              SupportTicketFilterNames.WORDS
            ] as ArrayStringFilterData,
          );
          addFilter(filter);
          break;
        }
        case "CUSTOMER_TAGS": {
          const filter: ArrayStringFilterData = deepCopyObject(
            supportTicketFilterData[
              SupportTicketFilterNames.CUSTOMER_TAGS
            ] as ArrayStringFilterData,
          );
          addFilter(filter);
          break;
        }
        case "CREATED_DATE": {
          const filter: DateFilterData = deepCopyObject(
            supportTicketFilterData[
              SupportTicketFilterNames.CREATED_AT
            ] as DateFilterData,
          );
          addFilter(filter);
          break;
        }
        case "LAST_RESPONSE_AT": {
          const filter: DateFilterData = deepCopyObject(
            supportTicketFilterData[
              SupportTicketFilterNames.LAST_RESPONSE_AT
            ] as DateFilterData,
          );
          addFilter(filter);
          break;
        }
        case "CLOSED_DATE": {
          const filter: DateFilterData = deepCopyObject(
            supportTicketFilterData[
              SupportTicketFilterNames.CLOSED_AT
            ] as DateFilterData,
          );
          addFilter(filter);
          break;
        }
        case "MENTIONS": {
          const filter: ArrayStringFilterData = deepCopyObject(
            supportTicketFilterData[
              SupportTicketFilterNames.MENTIONS
            ] as ArrayStringFilterData,
          );
          addFilter(filter);
          break;
        }
        default:
          assertNever(filterKey);
      }

      setFiltersDropdownOpen(false);
    },
  );

  return (
    <>
      <RedoListDropdown
        dropdownAnchor={filtersDropdownButtonRef}
        dropdownOpen={filtersDropdownOpen}
        fitToAnchor={false}
        items={FILTER_OPTIONS}
        itemSelected={handleAddFilter}
        setDropdownOpen={setFiltersDropdownOpen}
        size="xs"
      />
      <RedoButton
        hierarchy="secondary"
        IconLeading={FilterLinesIcon}
        onClick={() => setFiltersDropdownOpen((prev) => !prev)}
        ref={setFiltersDropdownButtonRef}
        size="sm"
      />
    </>
  );
});

export const SupportTicketsFilterToolbar = memo(
  function SupportTicketsFilterToolbar() {
    const { statuses, setStatuses } = useStatusFilter();
    const { filters, setFilters } = useAdvancedFilters();
    const statusCounts = useActiveViewStatusCounts();

    const wordsFilter = useMemo(() => {
      return filters.find(
        (f): f is ArrayStringFilterData =>
          f.name === SupportTicketFilterNames.WORDS,
      );
    }, [filters]);

    const wordsValue = useMemo(() => {
      if (!wordsFilter?.value || wordsFilter.value.length === 0) {
        return "";
      }
      return wordsFilter.value[0] ?? "";
    }, [wordsFilter]);

    const [searchString, setSearchString] = useState(wordsValue);
    const debouncedSearch = useDebounce(searchString, 500);

    const handleSearchChange = useHandler((value: string) => {
      setSearchString(value);
    });

    useEffect(() => {
      const currentSearch = wordsValue;
      if (debouncedSearch !== currentSearch) {
        setFilters((prev) => {
          const otherFilters = prev.filter(
            (f) => f.name !== SupportTicketFilterNames.WORDS,
          );
          if (debouncedSearch) {
            const newWordsFilter: ArrayStringFilterData = deepCopyObject(
              supportTicketFilterData[
                SupportTicketFilterNames.WORDS
              ] as ArrayStringFilterData,
            );
            newWordsFilter.value = [debouncedSearch];
            return [...otherFilters, newWordsFilter];
          }
          return otherFilters;
        });
      }
    }, [debouncedSearch, wordsValue, setFilters]);

    const handleClearSearch = useHandler(() => {
      setSearchString("");
      setFilters((prev) =>
        prev.filter((f) => f.name !== SupportTicketFilterNames.WORDS),
      );
    });

    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [statusDropdownButtonRef, setStatusDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);

    const statusOptionsWithCounts = useMemo<RedoListItem<StatusValue>[]>(
      () => [
        {
          id: "OPEN",
          value: "OPEN",
          type: "text",
          text: `Open (${statusCounts.OPEN})`,
        },
        {
          id: "CLOSED",
          value: "CLOSED",
          type: "text",
          text: `Closed (${statusCounts.CLOSED})`,
        },
        {
          id: "IN_PROGRESS",
          value: "IN_PROGRESS",
          type: "text",
          text: `In progress (${statusCounts.IN_PROGRESS})`,
        },
        {
          id: "SNOOZED",
          value: "SNOOZED",
          type: "text",
          text: `Snoozed (${statusCounts.SNOOZED})`,
        },
        {
          id: "DRAFT",
          value: "DRAFT",
          type: "text",
          text: `Draft (${statusCounts.DRAFT})`,
        },
      ],
      [statusCounts],
    );

    const isAllSelected = statuses.length === 0;

    const selectAllState = useMemo(() => {
      if (isAllSelected) {
        return true as const;
      }
      if (statuses.length < statusOptionsWithCounts.length) {
        return "indeterminate" as const;
      }
      return true as const;
    }, [isAllSelected, statuses.length, statusOptionsWithCounts.length]);

    const selectedStatusItems = useMemo<RedoListItem<StatusValue>[]>(
      () =>
        statuses
          .map((s) => statusOptionsWithCounts.find((o) => o.id === s))
          .filter((o): o is RedoListItem<StatusValue> => o !== undefined),
      [statuses, statusOptionsWithCounts],
    );

    const statusTriggerLabel = useMemo(() => {
      if (isAllSelected || statuses.length === statusOptionsWithCounts.length) {
        return "All";
      }
      if (statuses.length === 1) {
        const option = STATUS_OPTIONS.find((o) => o.id === statuses[0]);
        return (
          (option?.type === "text" ? option.text : null) ?? statuses[0] ?? "All"
        );
      }
      return `${statuses.length} statuses`;
    }, [isAllSelected, statuses, statusOptionsWithCounts.length]);

    const statusTriggerItem = useMemo<RedoListItem<string>>(
      () => ({
        id: "status-trigger",
        value: statusTriggerLabel,
        type: "text",
        text: statusTriggerLabel,
      }),
      [statusTriggerLabel],
    );

    const handleSelectAllToggled = useHandler(() => {
      setStatuses([]);
    });

    const handleStatusItemsSelected = useHandler(
      (items: RedoListItem<StatusValue>[]) => {
        if (items.length >= statusOptionsWithCounts.length) {
          setStatuses([]);
        } else {
          setStatuses(items.map((i) => i.value));
        }
      },
    );

    return (
      <Flex align="center" gap="md">
        <Flex w="xxs">
          <RedoTextInput
            fullWidth
            placeholder="Search"
            setValue={handleSearchChange}
            trailingActions={
              searchString ? (
                <Flex cursor="pointer" onClick={handleClearSearch}>
                  <XCloseIcon />
                </Flex>
              ) : undefined
            }
            value={searchString}
          />
        </Flex>
        <AddFilterButton />
        <Flex className="w-32">
          <RedoSingleSelectDropdownInputButton
            isDropdownOpen={statusDropdownOpen}
            onClick={() => setStatusDropdownOpen((prev) => !prev)}
            ref={setStatusDropdownButtonRef}
            selectedItem={statusTriggerItem}
            size="xs"
          />
          <RedoMultiselectDropdown<StatusValue>
            dropdownAnchor={statusDropdownButtonRef}
            dropdownOpen={statusDropdownOpen}
            fitToAnchor={false}
            items={statusOptionsWithCounts}
            selectAllLabel="All"
            selectAllState={selectAllState}
            selectAllToggled={handleSelectAllToggled}
            selectedItems={selectedStatusItems}
            setDropdownOpen={setStatusDropdownOpen}
            setSelectedItems={handleStatusItemsSelected}
            size="xs"
          />
        </Flex>
      </Flex>
    );
  },
);
